import { api } from "./apiClient.tsx";
import { handleApiError } from "./errorHandler.tsx";

import { RegisterData } from "../components/Register/Register.types.tsx";
import { LoginData } from "../components/Login/Login.types.tsx";

export const authRegister = async (registerData: RegisterData): Promise<string> => {
  try {
    const { data } = await api.post("/auth/register", {
      name: registerData.name,
      company: registerData.company,
      email: registerData.email,
      password: registerData.password,
      phone: registerData.phone,
      country: registerData.country,
    });

    if (!data?.token) {
      throw new Error("Token missing in response");
    }

    const token = data.token;

    return token;
  } catch (error: any) {
    throw handleApiError(error, {
      409: "The email is already registered",
    });
  }
};

export const authLogin = async (loginData: LoginData): Promise<string> => {
  try {
    const { data } = await api.post("/auth/login", {
      email: loginData.email,
      password: loginData.password,
    });

    if (!data?.token) {
      throw new Error("Token missing in response");
    }

    const token = data.token;

    return token;
  } catch (error: any) {
    throw handleApiError(error, {
      401: "The email or password is incorrect.",
      403: "This account has been disabled. Please contact support",
      404: "The email address is not registered",
      422: "The email address provided is not valid",
    });
  }
};

export const authActivateAccount = async (
  registerToken: string,
  verificationCode: string
): Promise<void> => {
  try {
    await api.post(
      "/auth/activate",
      { verificationCode },
      {
        headers: {
          Authorization: `Bearer ${registerToken}`,
        },
      }
    );
  } catch (error: any) {
    throw handleApiError(error, {
      400: "The provided verification code is invalid",
    });
  }
};

export const authForgotPassword = async (email: string): Promise<string> => {
  try {
    const { data } = await api.post("/auth/forgot-password", { email });

    if (!data?.uuid) {
      throw new Error("UUID missing in response");
    }

    return data.uuid;
  } catch (error: any) {
    throw handleApiError(error, {
      400: "The request body is invalid or improperly formatted",
      404: "The email address provided is not associated with any account.",
      500: "API Error",
    });
  }
};

export const authValidateCode = async (uuid: string, code: string): Promise<string> => {
  try {
    const { data } = await api.post("/auth/validate-code", {
      uuid,
      code,
    });

    if (!data?.token) {
      throw new Error("Token missing in response");
    }

    return data.token;
  } catch (error: any) {
    throw handleApiError(error, {
      400: "Invalid verification code",
    });
  }
};

export const authResetPassword = async (resetToken: string, newPassword: string): Promise<void> => {
  try {
    await api.post(
      "/auth/reset-password",
      { newPassword },
      {
        headers: {
          Authorization: `Bearer ${resetToken}`,
        },
      }
    );
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
    });
  }
};

export const authResendVerificationCode = async (): Promise<void> => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("Token not found");
    }

    await api.post("/auth/resend-code");
  } catch (error: any) {
    throw new Error("Failed to resend verification code");
  }
};
